'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useId, useRef, useState } from 'react';

import { routes } from '@endaoment-frontend/routes/server';

import { cn } from '../../cn';
import { ArrowIcon } from '../ArrowIcon';
import { BLOG_SECTION_ICONS } from '../BlogSectionIcons';
import { Card } from '../Card';
import { DownCaretIcon } from '../DownCaretIcon';
import { highlight } from '../Highlight';
import { HomeIcon } from '../HomeIcon';
import { ApiIcon, AppIcon, GivingServicesIcon } from '../PropertyIcons';

/**
 * The three doors, shared by the desktop and mobile navs.
 *
 * Each `summary` is the property descriptor from the 2026 Messaging Framework,
 * word for word and with nothing wrapped around it, and About takes the
 * Framework's one line. They stay single strings, so the screen-reader copy
 * cannot drift from them. `mark` lists the phrases that take the hue.
 *
 * `accent` and `current` spell their hue out, because Tailwind scans for whole
 * class names and a `text-${hue}` template generates no CSS.
 */
export const NAV_LINKS = [
  {
    label: 'App',
    href: routes.marketing.app(),
    Icon: AppIcon,
    summary: 'Self-service giving platform for donors, their family and advisors.',
    mark: ['Self-service giving platform'],
    accent: 'hover:text-orange focus-visible:text-orange',
    current: 'text-orange',
  },
  {
    label: 'API',
    href: routes.marketing.api(),
    Icon: ApiIcon,
    summary: 'Giving infrastructure designed for firms, products, and platforms.',
    mark: ['Giving infrastructure'],
    accent: 'hover:text-blue focus-visible:text-blue',
    current: 'text-blue',
  },
  {
    label: 'About',
    href: routes.marketing.about(),
    Icon: GivingServicesIcon,
    summary: 'Technology and services for turning investments into impact.',
    mark: ['investments into impact'],
    accent: 'hover:text-purple focus-visible:text-purple',
    current: 'text-purple',
  },
] as const;

/**
 * The blog's door, labelled Learn. Its hover shows the sections rather than a summary, and on
 * the blog itself the segment is always lit.
 *
 * The section labels are the names in apps/blog/src/lib/blog-sections.ts and
 * the footer's Learn column, and each summary is that file's tagline (the
 * section page's heading) in sentence case, without a full stop. The design
 * system cannot read that file; the blog's blog-sections test holds the lists
 * together. `also` lists the other pages a section owns: the three knowledge
 * base categories are tag pages of their own.
 */
export const BLOG_NAV = {
  label: 'Learn',
  href: routes.blog.home(),
  accent: 'hover:text-scarlet focus-visible:text-scarlet',
  current: 'text-scarlet',
  sections: [
    {
      label: 'Case Studies',
      href: routes.blog.caseStudies(),
      Icon: BLOG_SECTION_ICONS['case-studies'],
      summary: 'Established impact',
    },
    {
      label: 'Announcements',
      href: routes.blog.announcements(),
      Icon: BLOG_SECTION_ICONS.announcements,
      summary: 'Partner launches and company news',
    },
    {
      label: 'Release Notes',
      href: routes.blog.releaseNotes(),
      Icon: BLOG_SECTION_ICONS['release-notes'],
      summary: 'New features, bug fixes and more',
    },
    {
      label: 'Knowledge Base',
      href: routes.blog.knowledgeBase(),
      Icon: BLOG_SECTION_ICONS['knowledge-base'],
      summary: 'Learn about the power of DAFs',
      also: ['kb-basics', 'kb-tax', 'kb-pov'].map(slug => routes.blog.tag({ slug })),
    },
  ],
} as const;

/*
 * The hover fill is faded with opacity because its stops are
 * `color-mix(… currentColor …)`, and a registered custom property that depends
 * on currentColor cannot be interpolated: animating the stops jumps to full
 * strength instead of easing.
 */
const FILL = cn(
  'before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit]',
  'before:bg-linear-to-b before:from-current/18 before:to-current/5',
  'before:opacity-0 before:transition-opacity before:duration-200 before:ease-out',
  'hover:before:opacity-100 focus-visible:before:opacity-100',
);

/* The same fill at part strength, so pointing at the segment you are on does not
   stack a second one. */
const CURRENT = 'border-current/25 before:opacity-55';

/* The control's edge and the bubble's, named once so the two cannot drift. */
const EDGE = 'border-slate-400/75';
/* The same colour as the property icon's `--frame` (PropertyIcons.tsx), for the bubble. */
const EDGE_FRAME = '[--frame:color-mix(in_srgb,var(--color-slate-400)_75%,transparent)]';

/* A segment. The border is transparent until hover. The -1px margin lays it
   over the dividers and the row's outline, so a hovered segment is one line,
   not two. */
const SEGMENT = cn(
  'group relative flex items-center px-7 py-1.5 font-title text-lg font-semibold text-slate-800',
  'transition-colors duration-200 ease-out',
  '-m-px border border-transparent',
  FILL,
  'hover:border-current/50',
  'focus-visible:border-current/50',
);

/**
 * The nav lit for the page the reader is on. Its own component, because the
 * pathname is request data: under the blog's cacheComponents a layout that
 * reads it outside Suspense blocks the whole page, so SiteHeader streams this
 * in behind an unlit PrimaryNav.
 */
export const CurrentPrimaryNav = () => <PrimaryNav pathname={usePathname()} />;

/**
 * The bubble is aria-hidden and each summary is repeated in a visually hidden
 * span the link points at with aria-describedby. A hover-only tooltip reaches
 * neither a screen reader nor a keyboard user.
 *
 * Keep 'use client' out of SiteHeader: it would take the wordmark, the container
 * and the icons to the browser too.
 */
export const PrimaryNav = ({ pathname }: { pathname: string }) => {
  const descriptionId = useId();
  const [active, setActive] = useState<number | null>(null);
  /* Sticky, so the bubble fades out still showing what it showed. */
  const [shown, setShown] = useState(0);
  const [centre, setCentre] = useState(0);
  const items = useRef<Array<HTMLLIElement | null>>([]);

  const centreOf = (index: number) => {
    const item = items.current[index];
    return item ? item.offsetLeft + item.offsetWidth / 2 : 0;
  };

  const show = (index: number) => {
    setCentre(centreOf(index));
    setShown(index);
    setActive(index);
  };

  const { Icon: ShownIcon, summary: shownSummary, mark: shownMark, current: shownHue } = NAV_LINKS[shown];

  const home = routes.marketing.home();
  const onHome = pathname === home;
  /* Prefix match, so a sub-page keeps its property's segment lit. On the blog
     its own root is `/`, and every page there is under it. */
  const isCurrent = (href: string) => pathname === href || pathname.startsWith(href.endsWith('/') ? href : `${href}/`);
  const onBlog = isCurrent(BLOG_NAV.href);
  const section = BLOG_NAV.sections.find(
    entry => isCurrent(entry.href) || ('also' in entry && entry.also.some(isCurrent)),
  );

  return (
    <div className='relative hidden lg:block' onMouseLeave={() => setActive(null)}>
      {/* Segment padding holds the arrow when it appears, so no seam moves on
          hover. shadow-shallow and inset-shadow-lip compose rather than one
          winning, because Tailwind keeps inset shadows on their own property. */}
      <ul
        className={cn('flex list-none items-stretch rounded-lg border bg-white shadow-shallow inset-shadow-lip', EDGE)}>
        {/* Entering this link clears the bubble. The row's mouseleave cannot,
            because moving here never leaves the row. */}
        <li className='flex'>
          <Link
            href={home}
            aria-current={onHome ? 'page' : undefined}
            onMouseEnter={() => setActive(null)}
            onFocus={() => setActive(null)}
            className={cn(
              /* periwinkle-300 at rest, a pale version of the periwinkle it
                 takes on hover. */
              'group relative flex items-center rounded-l-lg px-6 py-1.5 text-periwinkle-300',
              '-m-px border border-transparent transition-colors duration-200 ease-out',
              FILL,
              'hover:border-current/50 hover:text-periwinkle',
              'focus-visible:border-current/50 focus-visible:text-periwinkle',
              onHome && cn('text-periwinkle', CURRENT),
            )}>
            {/* No opacity of its own. The colour above carries the resting
                weight, and a second knob doing the same job only made it
                lighter than the step it was set to. */}
            <HomeIcon />
            <span className='sr-only'>Home</span>
          </Link>
        </li>

        {NAV_LINKS.map(({ label, href, accent, current, summary }, index) => (
          <li
            key={href}
            ref={item => {
              items.current[index] = item;
            }}
            className='flex border-l border-slate-400/45'>
            <Link
              href={href}
              aria-current={isCurrent(href) ? 'page' : undefined}
              aria-describedby={`${descriptionId}-${index}`}
              onMouseEnter={() => show(index)}
              onFocus={() => show(index)}
              onBlur={() => setActive(null)}
              className={cn(SEGMENT, accent, isCurrent(href) && cn(current, CURRENT))}>
              <span className='relative transition-transform duration-200 ease-out motion-safe:group-hover:-translate-x-3 motion-safe:group-focus-visible:-translate-x-3'>
                {label}
                {/* Absolute, so the arrow costs no width. mt-px against top-1/2,
                    because the line box carries descender space the capitals do
                    not use. */}
                <ArrowIcon className='absolute top-1/2 left-full mt-px ml-1.5 size-4 -translate-y-1/2 opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-50 group-focus-visible:opacity-50' />
              </span>
              <span id={`${descriptionId}-${index}`} className='sr-only'>
                {summary}
              </span>
            </Link>
          </li>
        ))}

        {/* The sections open on hover and on focus within, so a keyboard
            reaches them by tabbing on from the segment. Entering the segment
            clears the bubble, like Home. */}
        <li className='group/blog relative flex border-l border-slate-400/45'>
          <Link
            href={BLOG_NAV.href}
            aria-current={onBlog ? 'page' : undefined}
            onMouseEnter={() => setActive(null)}
            onFocus={() => setActive(null)}
            className={cn(SEGMENT, 'rounded-r-lg', BLOG_NAV.accent, onBlog && cn(BLOG_NAV.current, CURRENT))}>
            {BLOG_NAV.label}
            <DownCaretIcon className='ml-2 transition-transform duration-200 ease-out group-hover/blog:rotate-180 group-focus-within/blog:rotate-180' />
          </Link>
          {/* Padding rather than a margin above the card, so the pointer can
              cross the gap without leaving the group. Hidden with visibility
              rather than display, so it can fade and settle in from a few
              pixels down, and fade back out; visibility flips at the end of
              the fade-out, and while hidden its links are out of the tab
              order. */}
          <div
            className={cn(
              'invisible absolute top-full left-1/2 -translate-x-1/2 translate-y-1 pt-4 opacity-0',
              'transition-[opacity,translate,visibility] duration-200 ease-out',
              'group-hover/blog:visible group-hover/blog:translate-y-0 group-hover/blog:opacity-100',
              'group-focus-within/blog:visible group-focus-within/blog:translate-y-0 group-focus-within/blog:opacity-100',
            )}>
            {/* Wide enough for the longest summary on one line at the label
                step: "What a DAF is, and how people use one." with the mark
                and the arrow beside it. */}
            <Card as='div' padding='none' className={cn('w-[26rem] flex-col p-2', EDGE)}>
              {/* No lines between items: the hover fill on each is enough to
                  tell them apart, and the rules made the menu read as a table. */}
              <ul className='m-0 flex list-none flex-col gap-1 p-0'>
                {BLOG_NAV.sections.map(entry => (
                  <li key={entry.href}>
                    {/* group/item, so the line under the name can take its own
                        shade of the hover the link takes. */}
                    <Link
                      href={entry.href}
                      aria-current={entry === section ? 'page' : undefined}
                      className={cn(
                        'group/item flex items-center gap-3 rounded-md py-2 pr-5 pl-3 font-title font-semibold text-slate-800 transition-colors duration-200 ease-out',
                        'hover:bg-scarlet-50 hover:text-scarlet-700 focus-visible:bg-scarlet-50 focus-visible:text-scarlet-700',
                        entry === section && 'text-scarlet-700',
                      )}>
                      <entry.Icon className='size-5 shrink-0 text-scarlet-700' />
                      <span className='min-w-0 flex-1'>
                        {entry.label}
                        <span className='label block text-slate-500 transition-colors duration-200 ease-out group-hover/item:text-scarlet-300 group-focus-visible/item:text-scarlet-300'>
                          {entry.summary}
                        </span>
                      </span>
                      <ArrowIcon className='size-4 shrink-0 text-scarlet-300 transition-[color,translate] duration-200 ease-out group-hover/item:translate-x-0.5 group-hover/item:text-scarlet-700 group-focus-visible/item:text-scarlet-700' />
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </li>
      </ul>

      {/* Mounted on the first hover, already positioned. An element does not
          transition from the style it was born with, so mounting it unpositioned
          slides it in from the nav's left edge. Positions are measured on the
          hover, so a resize cannot leave one stale. */}
      {centre > 0 ? (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute top-full left-0 transition duration-200 ease-out',
            active === null && 'opacity-0',
          )}
          style={{ translate: `${centre}px 0` }}>
          {/* 23rem: at 20rem the API summary ran to three lines beside its
              icon, and 22rem is where it drops to two, with a rem of slack so
              a word's width of copy change does not push it back to three.
              py-3 under the recipe's p-4: two lines beside a 50px icon need
              less air above and below. */}
          <Card padding='sm' className={cn('mt-4 w-[23rem] -translate-x-1/2 items-center gap-3 py-3', EDGE)}>
            {/* Rounded to 20%, so the shadow follows the icon rather than boxing
                it. */}
            <ShownIcon className={cn('size-12.5 shrink-0 rounded-[20%] shadow-shallow', EDGE_FRAME)} />
            <p className='paragraph m-0 text-slate-600'>
              {highlight(shownSummary, shownMark, cn('font-medium', shownHue))}
            </p>
          </Card>
        </div>
      ) : null}
    </div>
  );
};
